import { parseServerTimestamp } from '@/utils/time'
import { VIEWER_LATENCY_SECONDS } from '../limits'
import { sessionClockSeconds } from '../time'
import type { RoomState } from '../types'

/** Where things sit in the recording, in seconds from its first frame. */

/**
 * Where a live viewer is in the recording now: the session's clock (`sessionClockSeconds`), less the latency a
 * low-latency viewer sees it with. The heartbeat sends it, and the API keeps the last one as the registration's
 * `LastLivePositionSeconds`, which the replay picks up from. Null before the recording has played.
 */
export function liveViewerPositionSeconds(
    room: Pick<RoomState, 'VideoState' | 'PositionSeconds' | 'PositionReportedUtc' | 'RecordingStartedUtc' | 'RecordingDurationSeconds'>,
    nowMs: number,
): number | null {
    const clock = sessionClockSeconds(room, nowMs)
    return clock === null ? null : Math.max(0, clock - VIEWER_LATENCY_SECONDS)
}

/** A replay picking up where the viewer left the live session closer than this to its end starts from the start. */
export const RESUME_END_MARGIN_SECONDS = 30

export interface ReplayResume {
    startSeconds: number
    /** Where it picked up, for "Picked up at 23:14…"; null when it starts from the start. */
    pickedUpAtSeconds: number | null
}

/**
 * Where the replay starts for someone who watched live: where they left (`LastLivePositionSeconds`). Someone who
 * never watched, or left within the last half minute, starts from the start, as there is nothing left to pick up.
 */
export function replayResume(lastLivePositionSeconds: number | null | undefined, durationSeconds: number | null | undefined): ReplayResume {
    const last = lastLivePositionSeconds
    if (last == null || !Number.isFinite(last) || last < 1) return { startSeconds: 0, pickedUpAtSeconds: null }
    if (durationSeconds != null && last >= durationSeconds - RESUME_END_MARGIN_SECONDS) return { startSeconds: 0, pickedUpAtSeconds: null }
    const at = Math.floor(last)
    return { startSeconds: at, pickedUpAtSeconds: at }
}

/**
 * The moment in the recording a chat message was written against: its time less frame 0 and less the latency the
 * writer saw the stream with. Frame 0 is where the last stream run stamped it (`RecordingStartedUtc`), so a first run
 * that started late, which plays from the beginning, still lines up; without a stamp it is the start, where the
 * recording lands. A message from before a restart drifts by the outage, as the restart carries on from where it
 * stopped. Null outside the recording, where there is nothing to jump to.
 */
export function replaySecondsOfMessage(
    createdUtc: string,
    room: Pick<RoomState, 'RecordingStartedUtc' | 'StartUtc' | 'RecordingDurationSeconds'>,
): number | null {
    const createdMs = parseServerTimestamp(createdUtc)
    const frameZeroMs = parseServerTimestamp(room.RecordingStartedUtc) ?? parseServerTimestamp(room.StartUtc)
    if (createdMs === null || frameZeroMs === null) return null
    const seconds = Math.max(0, (createdMs - frameZeroMs) / 1000 - VIEWER_LATENCY_SECONDS)
    const duration = room.RecordingDurationSeconds
    return duration != null && seconds > duration ? null : Math.floor(seconds)
}

/** How far one arrow key moves a seek bar. */
export const SEEK_STEP_SECONDS = 10

/** A seek bar's keys: arrows ±10 s, Home the start, End the end. Null for any other key. */
export function seekTargetForKey(key: string, currentSeconds: number, durationSeconds: number): number | null {
    const clamp = (seconds: number) => Math.min(durationSeconds, Math.max(0, seconds))
    switch (key) {
        case 'ArrowRight':
        case 'ArrowUp':
            return clamp(currentSeconds + SEEK_STEP_SECONDS)
        case 'ArrowLeft':
        case 'ArrowDown':
            return clamp(currentSeconds - SEEK_STEP_SECONDS)
        case 'Home':
            return 0
        case 'End':
            return durationSeconds
        default:
            return null
    }
}

/** The moment under a pointer on a seek bar `width` wide starting at `left`, held to the bar's ends. */
export function seekTargetAt(clientX: number, left: number, width: number, durationSeconds: number): number {
    if (width <= 0) return 0
    return Math.min(1, Math.max(0, (clientX - left) / width)) * durationSeconds
}

/**
 * The length a player's bar shows: the room's, which the prepared file measured, while the player plays that file. Mux's
 * copy of it reads a few hundredths longer, so within a second it is the same file; further off, the player is still on
 * a file the room has since replaced, and its own reading matches what is on screen.
 */
export function shownLengthSeconds(roomSeconds: number | null | undefined, playerSeconds: number | null): number {
    if (roomSeconds == null || roomSeconds <= 0) return playerSeconds ?? 0
    if (playerSeconds === null || Math.abs(playerSeconds - roomSeconds) <= 1) return roomSeconds
    return playerSeconds
}

/** How much of the recording lies before a moment, as a fraction held to 0…1. */
export function playedFraction(seconds: number, durationSeconds: number | null): number {
    if (!durationSeconds || durationSeconds <= 0) return 0
    return Math.min(1, Math.max(0, seconds / durationSeconds))
}
